import * as React from 'react';
import { useEffect, useRef, useState, useMemo, useImperativeHandle, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { ToggleButton } from 'primereact/togglebutton';
import { ListEditDialog } from './ListEditDialog';
import { AppendixRegistryDialog } from './AppendixRegistryDialog';
import { AdminBlockEditorDialog } from './AdminBlockEditorDialog';
import type { FieldItem, AdminForm } from './EditorTypes';
import {
  APPENDIX_REGISTRY_ATTR,
  APPENDIX_REGISTRY_PAYLOAD_ATTR,
  APPENDIX_SYNC_ACT_DAY_VAR,
  APPENDIX_SYNC_ACT_MONTH_VAR,
  APPENDIX_SYNC_ACT_NO_VAR,
  APPENDIX_SYNC_ACT_YEAR_VAR,
  APPENDIX_SYNC_BUILDER_VAR,
  APPENDIX_SYNC_OBJECT_VAR,
  compactRegistryRows,
  createEmptyAppendixRegistryRow,
  decodeRegistryPayload,
  encodeRegistryPayload,
  removeAppendixSection,
  syncAppendixRegistryForRoot,
  type AppendixRegistryRow
} from './appendixRegistryHelpers';
import CodeMirror from '@uiw/react-codemirror';
import { html } from '@codemirror/lang-html';
import { EditorView } from '@codemirror/view';
import { alertWarn } from '../../store/reducers/Alert';
import { useDispatch } from 'react-redux';
import {
  applyVariableChipsToRoot,
  getElementRawText,
  getSerializedInnerHtml,
  setElementContent,
} from './templateVariableHelpers';

interface DocumentEditorComponentProps {
  initialHtmlContent?: string;
  adminMode?: boolean;
  onHtmlContentChange?: (htmlContent: string) => void;
  pageOrientation?: 'portrait' | 'landscape';
  readonly?: boolean;
}

export interface DocumentEditorRef {
  getCurrentHtml: () => string;
}

export const DocumentEditorComponent = forwardRef<DocumentEditorRef, DocumentEditorComponentProps>(({
  initialHtmlContent,
  adminMode = false,
  onHtmlContentChange,
  pageOrientation = 'portrait',
  readonly = false
}, ref) => {

  const dispatch = useDispatch();

  const [htmlContent, setHtmlContent] = useState<string>('');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [listEditVisible, setListEditVisible] = useState<boolean>(false);
  const [fields, setFields] = useState<FieldItem[]>([]);
  const [adminDialogVisible, setAdminDialogVisible] = useState<boolean>(false);
  const adminTargetElRef = useRef<HTMLElement | null>(null);
  const [adminForm, setAdminForm] = useState<AdminForm | null>(null);
  const [adminTargetKey, setAdminTargetKey] = useState<string | null>(null);
  const [templatePreviewHtml, setTemplatePreviewHtml] = useState<string>('');
  const [highlightAllElements, setHighlightAllElements] = useState<boolean>(false);
  const [editorReady, setEditorReady] = useState<boolean>(false);
  const [appendixRegistryVisible, setAppendixRegistryVisible] = useState<boolean>(false);
  const [appendixRegistryRows, setAppendixRegistryRows] = useState<AppendixRegistryRow[]>([]);
  const appendixRegistryElRef = useRef<HTMLElement | null>(null);

  const enumOverlayRef = useRef<OverlayPanel | null>(null);
  const enumTargetElRef = useRef<HTMLElement | null>(null);
  const [enumOptions, setEnumOptions] = useState<string[]>([]);
  const [enumSelected, setEnumSelected] = useState<string>('');

  const actionOverlayRef = useRef<OverlayPanel | null>(null);
  const actionTargetElRef = useRef<HTMLElement | null>(null);
  const [accordionActiveIndex, setAccordionActiveIndex] = useState<number | null>(null);
  const editorViewRef = useRef<any>(null);
  const [pendingSelection, setPendingSelection] = useState<{ from: number, to: number } | null>(null);
  const [rowButtons, setRowButtons] = useState<Array<{ element: HTMLElement; type: 'template' | 'removable'; position: { left: number; top: number } }>>([]);
  const [strikethroughButtons, setStrikethroughButtons] = useState<Array<{ element: HTMLElement; position: { left: number; top: number } }>>([]);
  const [lineThroughButtons, setLineThroughButtons] = useState<Array<{ element: HTMLElement; position: { left: number; top: number } }>>([]);
  const isLandscape = pageOrientation === 'landscape';

  const applyPendingSelectionNow = () => {
    if (accordionActiveIndex !== 0 || !pendingSelection) return;
    const view = editorViewRef.current;
    if (!view) return;
    const from = Math.max(0, Math.min(pendingSelection.from, view.state.doc.length));
    const to = Math.max(0, Math.min(pendingSelection.to, view.state.doc.length));
    view.dispatch({ selection: { anchor: from, head: to } });
    view.dispatch({ effects: EditorView.scrollIntoView(from, { y: 'center' }) });
    view.focus();
    setPendingSelection(null);
  };

  const dangerouslySetInnerHTML = useMemo(() => ({ __html: htmlContent }), [htmlContent]);

  // Expose method to get current HTML content
  useImperativeHandle(ref, () => ({
    getCurrentHtml: () => {
      const root = containerRef.current;
      if (root) {
        syncAppendixRegistryForRoot(root);
        return getSerializedInnerHtml(root);
      }
      return htmlContent;
    }
  }));

  const validateAndClamp = (el: HTMLElement) => {
    const type = el.getAttribute('data-type') || 'string';
    const maxLen = parseInt(el.getAttribute('data-maxlen') || '0', 10);
    const required = (el.getAttribute('data-required') || 'false') === 'true';
    let text = getElementRawText(el);

    if (maxLen > 0 && text.length > maxLen) {
      text = text.slice(0, maxLen);
    }

    let isValid = true;
    let message = '';

    if (required && text.length === 0) {
      isValid = false;
      message = 'Обязательное поле';
    }

    if (type === 'number') {
      const decimals = parseInt(el.getAttribute('data-decimals') || '0', 10);
      const min = el.getAttribute('data-min');
      const max = el.getAttribute('data-max');
      const normalized = text.replace(',', '.');
      const numberRegex = decimals > 0 ? new RegExp(`^-?\\d+(?:[.,]\\d{0,${decimals}})?$`) : /^-?\d+$/;
      if (text && !numberRegex.test(text)) {
        isValid = false;
        message = `Число, десятичных: ${decimals}`;
      }
      const value = Number(normalized);
      if (isValid && min !== null && min !== undefined && min !== '' && !Number.isNaN(value)) {
        if (value < Number(min)) {
          isValid = false;
          message = `Минимум: ${min}`;
        }
      }
      if (isValid && max !== null && max !== undefined && max !== '' && !Number.isNaN(value)) {
        if (value > Number(max)) {
          isValid = false;
          message = `Максимум: ${max}`;
        }
      }
      if (!Number.isNaN(value)) {
        setElementContent(el, decimals > 0 ? value.toString() : String(Math.trunc(value)));
      }
    } else if (type === 'enum') {
      const list = (el.getAttribute('data-enum') || '').split('|').map(s => s.trim().toLowerCase()).filter(Boolean);
      if (text && list.length > 0) {
        const ok = list.includes(text.toLowerCase());
        if (!ok) {
          isValid = false;
          message = 'Недопустимое значение';
        }
      }
    }

    if (getElementRawText(el) !== text) {
      setElementContent(el, text);
    }

    if (!isValid) {
      el.setAttribute('aria-invalid', 'true');
      if (message) el.setAttribute('title', message);
    } else {
      el.removeAttribute('aria-invalid');
      if (el.getAttribute('title')) el.removeAttribute('title');
    }
  };

  useEffect(() => {
    if (initialHtmlContent) {
      setHtmlContent(initialHtmlContent);
    }
  }, [initialHtmlContent]);

  useEffect(() => {
    if (onHtmlContentChange) {
      onHtmlContentChange(htmlContent);
    }
  }, [htmlContent, onHtmlContentChange]);

  /** Поддержка приложения реестра в актуальном DOM при изменении разметки */
  useEffect(() => {
    const root = containerRef.current;
    if (!root || !htmlContent) return;
    if (!root.querySelector(`[${APPENDIX_REGISTRY_ATTR}]`)) return;
    const before = root.innerHTML;
    syncAppendixRegistryForRoot(root);
    const after = root.innerHTML;
    if (after !== before) {
      setHtmlContent(after);
      setTemplatePreviewHtml(after);
    }
  }, [htmlContent]);

  useEffect(() => {
    if (accordionActiveIndex === 0 && pendingSelection && editorViewRef.current) {
      const t = setTimeout(() => applyPendingSelectionNow(), 0);
      return () => clearTimeout(t);
    }
  }, [accordionActiveIndex, pendingSelection]);

  useEffect(() => {
    if (editorReady) {
      const t = setTimeout(() => applyPendingSelectionNow(), 0);
      return () => clearTimeout(t);
    }
  }, [editorReady]);


  useEffect(() => {
    if (!htmlContent) return;
    const root = containerRef.current;
    if (!root) return;
    const editable = root.querySelectorAll<HTMLElement>('[data-editable="true"]');

    let updateTimeout: number | null = null;
    const updateHtmlContent = () => {
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
      updateTimeout = window.setTimeout(() => {
        if (root) {
          const updatedHtml = getSerializedInnerHtml(root);
          setHtmlContent(updatedHtml);
        }
      }, 100);
    };

    const handleClickOutside = (e: Event) => {
      const target = e.target as HTMLElement;
      const isEditable = target.closest('[data-editable="true"]');
      if (!isEditable) {
        updateHtmlContent();
      }
    };

    editable.forEach((el) => {
      if (readonly) {
        // В режиме readonly явно отключаем редактирование
        el.setAttribute('contenteditable', 'false');
        el.classList.remove('editable');
        // Удаляем все обработчики событий, если они были добавлены ранее
        const existingHandler = (el as any).__onEnumClick;
        if (existingHandler) {
          el.removeEventListener('click', existingHandler);
          delete (el as any).__onEnumClick;
        }
        const appendixHandler = (el as any).__onAppendixRegistryClick as ((e: Event) => void) | undefined;
        if (appendixHandler) {
          el.removeEventListener('click', appendixHandler);
          delete (el as any).__onAppendixRegistryClick;
        }
        return;
      }

      if (!adminMode && el.hasAttribute(APPENDIX_REGISTRY_ATTR)) {
        el.setAttribute('contenteditable', 'false');
        el.classList.add('editable');
        const onAppendixRegistryClick = (e: Event) => {
          e.preventDefault();
          e.stopPropagation();
          appendixRegistryElRef.current = el;
          let rows = decodeRegistryPayload(el.getAttribute(APPENDIX_REGISTRY_PAYLOAD_ATTR));
          if (rows.length === 0) {
            rows = [createEmptyAppendixRegistryRow()];
          }
          setAppendixRegistryRows(rows);
          setAppendixRegistryVisible(true);
        };
        el.addEventListener('click', onAppendixRegistryClick);
        (el as any).__onAppendixRegistryClick = onAppendixRegistryClick;
        return;
      }

      const type = el.getAttribute('data-type') || 'string';
      const isEnum = type === 'enum';

      if (!adminMode && isEnum) {
        el.setAttribute('contenteditable', 'false');
        el.classList.add('editable');
        const onEnumClick = (e: Event) => {
          const enumText = el.getAttribute('data-enum') || '';
          const options = enumText.split('|').map(s => s.trim()).filter(Boolean);
          setEnumOptions(options);
          const currentValue = getElementRawText(el);
          setEnumSelected(currentValue);
          enumTargetElRef.current = el;
          enumOverlayRef.current?.show(e as any, el);
        };
        el.addEventListener('click', onEnumClick);
        (el as any).__onEnumClick = onEnumClick;
      } else {
        el.setAttribute('contenteditable', 'true');
        el.classList.add('editable');

        const handleEditablePaste = (e: ClipboardEvent) => {
          e.preventDefault();
          e.stopPropagation();
          let text = e.clipboardData?.getData('text/plain') ?? '';
          if (text === '') return;
          text = text.replace(/\r\n|\r|\n/g, ' ');

          if (type === 'number') {
            const decimals = parseInt(el.getAttribute('data-decimals') || '0', 10);
            let filtered = '';
            let hasDot = false;
            for (const ch of text) {
              if (/[0-9]/.test(ch)) {
                filtered += ch;
              } else if ((ch === ',' || ch === '.') && decimals > 0 && !hasDot) {
                filtered += '.';
                hasDot = true;
              } else if (ch === '-' && filtered.length === 0) {
                filtered += ch;
              }
            }
            if (decimals === 0) {
              filtered = filtered.replace(/\./g, '');
            } else {
              const dot = filtered.indexOf('.');
              if (dot !== -1) {
                filtered = filtered.slice(0, dot + 1 + decimals);
              }
            }
            text = filtered;
          }

          const maxLen = parseInt(el.getAttribute('data-maxlen') || '0', 10);
          if (maxLen > 0) {
            const currentText = getElementRawText(el);
            const selection = window.getSelection();
            let selectedLen = 0;
            if (selection && selection.rangeCount > 0) {
              const range = selection.getRangeAt(0);
              if (el.contains(range.commonAncestorContainer)) {
                selectedLen = range.toString().length;
              }
            }
            const room = maxLen - (currentText.length - selectedLen);
            if (room <= 0) {
              dispatch(alertWarn({
                summary: 'Превышена максимальная длина',
                detail: `Максимальная длина поля: ${maxLen} символов`,
                life: 3000
              }));
              return;
            }
            if (text.length > room) {
              text = text.slice(0, room);
              dispatch(alertWarn({
                summary: 'Превышена максимальная длина',
                detail: `Максимальная длина поля: ${maxLen} символов`,
                life: 3000
              }));
            }
          }

          const sel = window.getSelection();
          if (!sel || sel.rangeCount === 0 || !el.contains(sel.anchorNode)) {
            const range = document.createRange();
            range.selectNodeContents(el);
            range.collapse(false);
            sel?.removeAllRanges();
            sel?.addRange(range);
          }

          document.execCommand('insertText', false, text);
          updateHtmlContent();
        };
        el.addEventListener('paste', handleEditablePaste, true);
        (el as any).__onEditablePaste = handleEditablePaste;

        el.addEventListener('beforeinput', (e: any) => {
          const maxLen = parseInt(el.getAttribute('data-maxlen') || '0', 10);
          if (type === 'number') {
            const data = e.data as string | null;
            if (data && !/[0-9,\.\-]/.test(data)) e.preventDefault();
            const decimals = parseInt(el.getAttribute('data-decimals') || '0', 10);
            if (decimals === 0 && data === ',') e.preventDefault();
          }
          if (maxLen > 0) {
            const currentText = getElementRawText(el);
            const inputData = e.data || '';
            const inputType = e.inputType;

            // Вычисляем длину после ввода
            let newLength = currentText.length;
            if (inputType === 'insertText' || inputType === 'insertCompositionText') {
              newLength = currentText.length + inputData.length;
            } else if (inputType === 'insertFromPaste' || inputType === 'insertFromDrop') {
              // Для вставки нужно учесть выделенный текст, который будет заменен
              const selection = window.getSelection();
              if (selection && selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                const selectedText = range.toString();
                newLength = currentText.length - selectedText.length + inputData.length;
              } else {
                newLength = currentText.length + inputData.length;
              }
            }

            if (newLength > maxLen) {
              console.log('Превышена максимальная длина', newLength, maxLen);
              e.preventDefault();
              // Показываем предупреждение о превышении максимальной длины
              dispatch(alertWarn({
                summary: 'Превышена максимальная длина',
                detail: `Максимальная длина поля: ${maxLen} символов`,
                life: 3000
              }));
            }
          }
        });
        el.addEventListener('keydown', (e) => {
          // if (e.key === 'Enter') {
          //   e.preventDefault();
          //   el.blur();
          // }
          if (e.key === 'Escape') {
            e.preventDefault();
            el.blur();
          }
        });
      }
    });

    applyVariableChipsToRoot(root);

    root.addEventListener('click', handleClickOutside);

    return () => {
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
      editable.forEach((el) => {
        const appendixHandler = (el as any).__onAppendixRegistryClick as ((e: Event) => void) | undefined;
        if (appendixHandler) {
          el.removeEventListener('click', appendixHandler);
          delete (el as any).__onAppendixRegistryClick;
        }
        const type = el.getAttribute('data-type') || 'string';
        const isEnum = type === 'enum';
        if (!adminMode && isEnum) {
          const handler = (el as any).__onEnumClick as (e: Event) => void;
          if (handler) el.removeEventListener('click', handler);
          delete (el as any).__onEnumClick;
        } else {
          const pasteHandler = (el as any).__onEditablePaste as ((e: ClipboardEvent) => void) | undefined;
          if (pasteHandler) {
            el.removeEventListener('paste', pasteHandler, true);
            delete (el as any).__onEditablePaste;
          }
        }
        // При readonly уже установлено contenteditable="false", но на всякий случай удаляем
        if (readonly) {
          el.setAttribute('contenteditable', 'false');
        } else {
          el.removeAttribute('contenteditable');
        }
        el.classList.remove('editable');
      });
      root.removeEventListener('click', handleClickOutside);
    };
  }, [htmlContent, adminMode, readonly]);

  // Обработка строк таблицы с атрибутом data-row-template
  useEffect(() => {
    if (!htmlContent || readonly) {
      setRowButtons([]);
      return;
    }
    const root = containerRef.current;
    if (!root) return;

    let updateTimeout: number | null = null;
    const updateHtmlContent = () => {
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
      updateTimeout = window.setTimeout(() => {
        if (root) {
          const updatedHtml = getSerializedInnerHtml(root);
          setHtmlContent(updatedHtml);
        }
      }, 100);
    };

    const handleAddRow = (row: HTMLTableRowElement) => {
      const clonedRow = row.cloneNode(true) as HTMLTableRowElement;
      clonedRow.removeAttribute('data-row-template');
      clonedRow.setAttribute('data-removable', 'true');

      // Генерируем уникальный суффикс для полей в новой строке
      const generatedKey = `field_${Date.now()}`;

      // Очищаем значения в скопированной строке и сохраняем все атрибуты
      const editableElements = clonedRow.querySelectorAll<HTMLElement>('[data-editable="true"]');
      editableElements.forEach((el) => {
        // Сохраняем все data-атрибуты
        const dataAttrs: { [key: string]: string } = {};
        Array.from(el.attributes).forEach(attr => {
          if (attr.name.startsWith('data-')) {
            dataAttrs[attr.name] = attr.value;
          }
        });

        // Генерируем уникальный ключ для data-var, если он есть
        if (dataAttrs['data-var']) {
          dataAttrs['data-var'] = generatedKey;
        }

        // Очищаем содержимое
        el.textContent = '';

        // Восстанавливаем атрибуты
        Object.keys(dataAttrs).forEach(key => {
          el.setAttribute(key, dataAttrs[key]);
        });

        // Устанавливаем contenteditable в зависимости от типа
        const type = el.getAttribute('data-type') || 'string';
        const isEnum = type === 'enum';
        if (!adminMode && isEnum) {
          el.setAttribute('contenteditable', 'false');
        } else {
          el.setAttribute('contenteditable', 'true');
        }
        el.classList.add('editable');
      });

      // data-row-template-after-row — число фиксированных tr после шаблона (строки подписи и т.п.).
      // Новые строки вставляются после этого блока и после уже добавленных data-removable.
      const afterRows = Math.max(0, parseInt(row.getAttribute('data-row-template-after-row') || '0', 10));
      let anchor: Element | null = row;
      for (let i = 0; i < afterRows && anchor?.nextElementSibling; i++) {
        anchor = anchor.nextElementSibling;
      }
      let insertAfter: Element | null = anchor;
      let nextRow = insertAfter?.nextElementSibling ?? null;
      while (nextRow instanceof HTMLTableRowElement && nextRow.hasAttribute('data-removable')) {
        insertAfter = nextRow;
        nextRow = insertAfter.nextElementSibling;
      }
      const insertBeforeNode = insertAfter?.nextSibling ?? null;
      row.parentNode?.insertBefore(clonedRow, insertBeforeNode);
      updateHtmlContent();
    };

    const handleRemoveRow = (row: HTMLTableRowElement) => {
      row.remove();
      updateHtmlContent();
    };

    const updateButtonPositions = () => {
      const templateRows = root.querySelectorAll<HTMLTableRowElement>('tr[data-row-template]');
      const removableRows = root.querySelectorAll<HTMLTableRowElement>('tr[data-removable]');
      const buttons: Array<{ element: HTMLElement; type: 'template' | 'removable'; position: { left: number; top: number } }> = [];

      templateRows.forEach((row) => {
        if (!row.style.position) {
          row.style.position = 'relative';
        }
        const rect = row.getBoundingClientRect();
        buttons.push({
          element: row,
          type: 'template',
          position: {
            left: rect.left - 32,
            top: rect.top + rect.height / 2,
          }
        });
      });

      removableRows.forEach((row) => {
        if (!row.style.position) {
          row.style.position = 'relative';
        }
        const rect = row.getBoundingClientRect();
        buttons.push({
          element: row,
          type: 'removable',
          position: {
            left: rect.left - 32,
            top: rect.top + rect.height / 2,
          }
        });
      });

      setRowButtons(buttons);
    };

    // Сохраняем обработчики для использования в кнопках
    (root as any).__handleAddRow = handleAddRow;
    (root as any).__handleRemoveRow = handleRemoveRow;

    updateButtonPositions();

    const handleScroll = () => {
      requestAnimationFrame(updateButtonPositions);
    };
    const handleResize = () => {
      requestAnimationFrame(updateButtonPositions);
    };

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    // Отслеживаем изменения в DOM для обновления позиций кнопок
    const observer = new MutationObserver(() => {
      requestAnimationFrame(updateButtonPositions);
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: false,
    });

    return () => {
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
      delete (root as any).__handleAddRow;
      delete (root as any).__handleRemoveRow;
    };
  }, [htmlContent, adminMode, readonly]);

  // Обработка элементов с атрибутом data-strikethrough-zone
  useEffect(() => {
    if (!htmlContent || readonly) {
      setStrikethroughButtons([]);
      return;
    }
    const root = containerRef.current;
    if (!root) return;

    let updateTimeout: number | null = null;
    const updateHtmlContent = () => {
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
      updateTimeout = window.setTimeout(() => {
        if (root) {
          const updatedHtml = getSerializedInnerHtml(root);
          setHtmlContent(updatedHtml);
        }
      }, 100);
    };

    const handleToggleStrikethrough = (el: HTMLElement) => {
      const isStriked = el.classList.contains('z-strikethrough');
      if (isStriked) {
        el.classList.remove('z-strikethrough');
      } else {
        el.classList.add('z-strikethrough');
      }
      updateHtmlContent();
    };

    const updateButtonPositions = () => {
      const strikethroughElements = root.querySelectorAll<HTMLElement>('[data-strikethrough-zone]');
      const buttons: Array<{ element: HTMLElement; position: { left: number; top: number } }> = [];

      strikethroughElements.forEach((el) => {
        if (!el.style.position) {
          el.style.position = 'relative';
        }
        const rect = el.getBoundingClientRect();
        buttons.push({
          element: el,
          position: {
            left: rect.right + 8,
            top: rect.top + rect.height / 2,
          }
        });
      });

      setStrikethroughButtons(buttons);
    };

    // Сохраняем обработчик для использования в кнопках
    (root as any).__handleToggleStrikethrough = handleToggleStrikethrough;

    updateButtonPositions();

    const handleScroll = () => {
      requestAnimationFrame(updateButtonPositions);
    };
    const handleResize = () => {
      requestAnimationFrame(updateButtonPositions);
    };

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    // Отслеживаем изменения в DOM для обновления позиций кнопок
    const observer = new MutationObserver(() => {
      requestAnimationFrame(updateButtonPositions);
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-strikethrough-zone', 'class'],
    });

    return () => {
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
      delete (root as any).__handleToggleStrikethrough;
    };
  }, [htmlContent, readonly]);

  // Обработка элементов с атрибутом data-strikethrough-line (зачеркивание через text-decoration)
  useEffect(() => {
    if (!htmlContent || readonly) {
      setLineThroughButtons([]);
      return;
    }
    const root = containerRef.current;
    if (!root) return;

    let updateTimeout: number | null = null;
    const updateHtmlContent = () => {
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
      updateTimeout = window.setTimeout(() => {
        if (root) {
          const updatedHtml = getSerializedInnerHtml(root);
          setHtmlContent(updatedHtml);
        }
      }, 100);
    };

    const handleToggleLineThrough = (el: HTMLElement) => {
      const isStriked = el.classList.contains('z-line-through');
      if (isStriked) {
        el.classList.remove('z-line-through');
      } else {
        el.classList.add('z-line-through');
      }
      updateHtmlContent();
    };

    const updateButtonPositions = () => {
      const lineThroughElements = root.querySelectorAll<HTMLElement>('[data-strikethrough-line]');
      const buttons: Array<{ element: HTMLElement; position: { left: number; top: number } }> = [];

      lineThroughElements.forEach((el) => {
        if (!el.style.position) {
          el.style.position = 'relative';
        }
        const rect = el.getBoundingClientRect();
        buttons.push({
          element: el,
          position: {
            left: rect.right + 0,
            top: rect.top + rect.height / 2,
          }
        });
      });

      setLineThroughButtons(buttons);
    };

    // Сохраняем обработчик для использования в кнопках
    (root as any).__handleToggleLineThrough = handleToggleLineThrough;

    updateButtonPositions();

    const handleScroll = () => {
      requestAnimationFrame(updateButtonPositions);
    };
    const handleResize = () => {
      requestAnimationFrame(updateButtonPositions);
    };

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    // Отслеживаем изменения в DOM для обновления позиций кнопок
    const observer = new MutationObserver(() => {
      requestAnimationFrame(updateButtonPositions);
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-strikethrough-line', 'class'],
    });

    return () => {
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
      // Удаляем обработчики клавиш
      const lineThroughElements = root.querySelectorAll<HTMLElement>('[data-strikethrough-line]');
      lineThroughElements.forEach((el) => {
        const handler = (el as any).__onLineThroughKeyDown as ((e: KeyboardEvent) => void) | undefined;
        if (handler) {
          el.removeEventListener('keydown', handler);
          delete (el as any).__onLineThroughKeyDown;
        }
      });
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
      delete (root as any).__handleToggleLineThrough;
    };
  }, [htmlContent, readonly]);

  useEffect(() => {
    if (!adminMode) return;
    const onKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.altKey && (e.key === 'm' || e.key === 'M' || e.key === 'ь' || e.key === 'Ь')) {
        e.preventDefault();
        setHighlightAllElements(prev => !prev);
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [adminMode]);


  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const onClickCapture = (e: Event) => {
      if (!adminMode) return;
      const targetNode = e.target as Node;

      let leafEl: HTMLElement | null = null;
      try {
        const me = e as unknown as MouseEvent;
        const anyDoc = document as any;
        let leafNode: Node | null = null;
        if (anyDoc.caretRangeFromPoint) {
          const range = anyDoc.caretRangeFromPoint(me.clientX, me.clientY);
          leafNode = range ? range.startContainer : null;
        } else if (anyDoc.caretPositionFromPoint) {
          const pos = anyDoc.caretPositionFromPoint(me.clientX, me.clientY);
          leafNode = pos ? pos.offsetNode : null;
        }
        if (leafNode) {
          if (leafNode.nodeType === 3) {
            leafEl = (leafNode as any).parentElement as HTMLElement | null;
          } else if (leafNode.nodeType === 1) {
            leafEl = leafNode as HTMLElement;
          }
        }
      } catch { }

      if (!leafEl) {
        leafEl = (targetNode.nodeType === 1 ? (targetNode as HTMLElement) : (targetNode as any).parentElement) as HTMLElement | null;
      }

      let el = (leafEl && (leafEl.closest('[data-var],[data-editable]') as HTMLElement | null)) || null;

      if (!el) {
        if (root && leafEl && leafEl !== root && root.contains(leafEl)) {
          el = leafEl;
        }
      }

      if (!el || el === root) return;

      e.preventDefault();
      e.stopPropagation();
      actionTargetElRef.current = el;
      actionOverlayRef.current?.show(e as any, el);
    };
    if (adminMode) {
      root.addEventListener('click', onClickCapture, true);
    }
    return () => {
      root.removeEventListener('click', onClickCapture, true);
    };
  }, [adminMode, htmlContent]);

  const updateTemplatePreview = () => {
    const root = containerRef.current;
    if (root) setTemplatePreviewHtml(getSerializedInnerHtml(root));
  };

  const findElementByKey = (key: string | null): HTMLElement | null => {
    const root = containerRef.current;
    if (!root || !key) return null;
    return root.querySelector<HTMLElement>(`[data-var="${CSS.escape(key)}"]`);
  };

  const openAdminForElement = (el: HTMLElement) => {
    adminTargetElRef.current = el;
    const key = el.getAttribute('data-var') || '';
    const labelAttr = el.getAttribute('data-label') || '';
    const type = (el.getAttribute('data-type') as AdminForm['type']) || 'string';
    const required = el.hasAttribute('data-required') ? (el.getAttribute('data-required') === 'true') : true;
    const maxLen = parseInt(el.getAttribute('data-maxlen') || '0', 10);
    const min = el.getAttribute('data-min');
    const max = el.getAttribute('data-max');
    const decimals = parseInt(el.getAttribute('data-decimals') || '0', 10);
    const enumText = el.getAttribute('data-enum') || '';
    const tooltip = el.getAttribute('data-tooltip') || '';
    const disabled = el.getAttribute('data-editable') === 'false';
    const value = getElementRawText(el);
    setAdminTargetKey(key);
    setAdminForm({
      key,
      label: labelAttr,
      type,
      required,
      maxLen,
      min,
      max,
      decimals,
      enumText,
      tooltip,
      disabled,
      value,
      appendixRegistryEnabled: el.hasAttribute(APPENDIX_REGISTRY_ATTR),
      appendixRegistryAppendixId: el.getAttribute(APPENDIX_REGISTRY_ATTR) || '1',
      appendixSyncObjectVar: el.getAttribute(APPENDIX_SYNC_OBJECT_VAR) || '',
      appendixSyncBuilderVar: el.getAttribute(APPENDIX_SYNC_BUILDER_VAR) || '',
      appendixSyncActNoVar: el.getAttribute(APPENDIX_SYNC_ACT_NO_VAR) || '',
      appendixSyncActDayVar: el.getAttribute(APPENDIX_SYNC_ACT_DAY_VAR) || '',
      appendixSyncActMonthVar: el.getAttribute(APPENDIX_SYNC_ACT_MONTH_VAR) || '',
      appendixSyncActYearVar: el.getAttribute(APPENDIX_SYNC_ACT_YEAR_VAR) || ''
    });
    updateTemplatePreview();
    setAdminDialogVisible(true);
  };

  const applyAdminFormToDom = (next: AdminForm) => {
    const el = findElementByKey(adminTargetKey) || adminTargetElRef.current;
    if (!el) return;
    const generatedKey = `field_${Date.now()}`;
    const resolvedKey = (next.key && next.key.trim().length > 0) ? next.key.trim() : (adminTargetKey && adminTargetKey.trim().length > 0 ? adminTargetKey : generatedKey);
    if (resolvedKey !== adminTargetKey) {
      el.setAttribute('data-var', resolvedKey);
      setAdminTargetKey(resolvedKey);
      if ((!next.key || next.key.trim().length === 0)) {
        setAdminForm({ ...next, key: resolvedKey });
      }
    } else if (!el.getAttribute('data-var')) {
      el.setAttribute('data-var', resolvedKey);
    }
    if (next.label) el.setAttribute('data-label', next.label);
    else el.removeAttribute('data-label');
    el.setAttribute('data-type', next.type);
    el.setAttribute('data-required', next.required ? 'true' : 'false');
    el.setAttribute('data-maxlen', String(Number.isFinite(next.maxLen) ? next.maxLen : 0));
    if (next.type === 'number') {
      if (next.min !== undefined && next.min !== null && next.min !== '') el.setAttribute('data-min', String(next.min));
      else el.removeAttribute('data-min');
      if (next.max !== undefined && next.max !== null && next.max !== '') el.setAttribute('data-max', String(next.max));
      else el.removeAttribute('data-max');
      el.setAttribute('data-decimals', String(Number.isFinite(next.decimals || 0) ? (next.decimals || 0) : 0));
      el.removeAttribute('data-enum');
    } else if (next.type === 'enum') {
      const enumVal = (next.enumText || '').trim();
      if (enumVal) el.setAttribute('data-enum', enumVal);
      else el.removeAttribute('data-enum');
      el.removeAttribute('data-min');
      el.removeAttribute('data-max');
      el.removeAttribute('data-decimals');
    } else {
      el.removeAttribute('data-enum');
      el.removeAttribute('data-min');
      el.removeAttribute('data-max');
      el.removeAttribute('data-decimals');
    }
    if ((next.tooltip || '').trim()) el.setAttribute('data-tooltip', next.tooltip!.trim());
    else el.removeAttribute('data-tooltip');

    const rootApply = containerRef.current;
    if (next.appendixRegistryEnabled) {
      const aid = (next.appendixRegistryAppendixId || '1').trim() || '1';
      el.setAttribute(APPENDIX_REGISTRY_ATTR, aid);
      const syncAttr = (attr: string, v?: string) => {
        const t = (v || '').trim();
        if (t) el.setAttribute(attr, t);
        else el.removeAttribute(attr);
      };
      syncAttr(APPENDIX_SYNC_OBJECT_VAR, next.appendixSyncObjectVar);
      syncAttr(APPENDIX_SYNC_BUILDER_VAR, next.appendixSyncBuilderVar);
      syncAttr(APPENDIX_SYNC_ACT_NO_VAR, next.appendixSyncActNoVar);
      syncAttr(APPENDIX_SYNC_ACT_DAY_VAR, next.appendixSyncActDayVar);
      syncAttr(APPENDIX_SYNC_ACT_MONTH_VAR, next.appendixSyncActMonthVar);
      syncAttr(APPENDIX_SYNC_ACT_YEAR_VAR, next.appendixSyncActYearVar);
      el.removeAttribute('data-appendix-sync-act-date-var');
    } else {
      const prevAid = el.getAttribute(APPENDIX_REGISTRY_ATTR);
      el.removeAttribute(APPENDIX_REGISTRY_ATTR);
      el.removeAttribute(APPENDIX_REGISTRY_PAYLOAD_ATTR);
      el.removeAttribute(APPENDIX_SYNC_OBJECT_VAR);
      el.removeAttribute(APPENDIX_SYNC_BUILDER_VAR);
      el.removeAttribute(APPENDIX_SYNC_ACT_NO_VAR);
      el.removeAttribute(APPENDIX_SYNC_ACT_DAY_VAR);
      el.removeAttribute(APPENDIX_SYNC_ACT_MONTH_VAR);
      el.removeAttribute(APPENDIX_SYNC_ACT_YEAR_VAR);
      el.removeAttribute('data-appendix-sync-act-date-var');
      if (rootApply && prevAid) {
        removeAppendixSection(rootApply, prevAid);
      }
    }

    if (next.disabled) {
      el.setAttribute('data-editable', 'false');
      el.removeAttribute('contenteditable');
      el.classList.remove('editable');
    } else {
      el.setAttribute('data-editable', 'true');
      el.setAttribute('contenteditable', 'true');
      el.classList.add('editable');
    }
    if (next.value !== undefined && next.value !== null) {
      setElementContent(el, next.value);
    }
    validateAndClamp(el);
    const rootAfterApply = containerRef.current;
    if (rootAfterApply) {
      syncAppendixRegistryForRoot(rootAfterApply);
    }
    updateTemplatePreview();
  };

  const handleAdminFormChange = <K extends keyof AdminForm>(key: K, value: AdminForm[K]) => {
    if (!adminForm) return;
    const next = { ...adminForm, [key]: value };

    if ((key === 'type' && value === 'enum' && next.enumText) ||
      (key === 'enumText' && next.type === 'enum' && next.enumText)) {
      const enumValues = next.enumText.split('|').map(s => s.trim()).filter(Boolean);
      const currentValue = next.value || '';

      if (currentValue && !enumValues.includes(currentValue)) {
        next.value = '';
      }
    }

    setAdminForm(next);
  };

  const deleteSelectedElement = () => {
    const el = findElementByKey(adminTargetKey) || adminTargetElRef.current;
    if (!el) return;
    const rootDel = containerRef.current;
    const appendixId = el.getAttribute(APPENDIX_REGISTRY_ATTR);
    if (rootDel && appendixId) {
      removeAppendixSection(rootDel, appendixId);
    }
    const attrs = [
      'data-editable', 'data-var', 'data-label', 'data-type', 'data-required', 'data-maxlen',
      'data-min', 'data-max', 'data-decimals', 'data-enum', 'data-tooltip',
      APPENDIX_REGISTRY_ATTR,
      APPENDIX_REGISTRY_PAYLOAD_ATTR,
      APPENDIX_SYNC_OBJECT_VAR,
      APPENDIX_SYNC_BUILDER_VAR,
      APPENDIX_SYNC_ACT_NO_VAR,
      APPENDIX_SYNC_ACT_DAY_VAR,
      APPENDIX_SYNC_ACT_MONTH_VAR,
      APPENDIX_SYNC_ACT_YEAR_VAR,
      'data-appendix-sync-act-date-var'
    ];
    attrs.forEach(a => el.removeAttribute(a));
    el.removeAttribute('contenteditable');
    el.classList.remove('editable');
    setAdminForm(null);
    setAdminDialogVisible(false);
    const root = containerRef.current;
    if (root) {
      const updatedHtml = getSerializedInnerHtml(root);
      setHtmlContent(updatedHtml);
      setTemplatePreviewHtml(updatedHtml);
    }
  };

  const submitTemplateToServer = async () => {
    if (adminForm) {
      applyAdminFormToDom(adminForm);
    }
    const root = containerRef.current;
    if (root) {
      const updatedHtml = getSerializedInnerHtml(root);
      setTemplatePreviewHtml(updatedHtml);
      setHtmlContent(updatedHtml);
    }
    setAdminDialogVisible(false);
  };

  const handleAdminHide = () => {
    setAdminDialogVisible(false);
  };

  const applyAppendixRegistry = () => {
    const root = containerRef.current;
    const el = appendixRegistryElRef.current;
    if (!root || !el || !root.contains(el)) {
      setAppendixRegistryVisible(false);
      return;
    }
    const rows = compactRegistryRows(appendixRegistryRows);
    el.setAttribute(APPENDIX_REGISTRY_PAYLOAD_ATTR, encodeRegistryPayload(rows));
    syncAppendixRegistryForRoot(root);
    const updatedHtml = getSerializedInnerHtml(root);
    setHtmlContent(updatedHtml);
    setTemplatePreviewHtml(updatedHtml);
    setAppendixRegistryVisible(false);
  };

  const openListEdit = () => {
    const root = containerRef.current;
    if (!root) return;
    const editable = root.querySelectorAll<HTMLElement>('[data-editable="true"][data-var]');
    const items: FieldItem[] = [];
    editable.forEach((el) => {
      if (el.hasAttribute(APPENDIX_REGISTRY_ATTR)) return;
      const key = el.getAttribute('data-var') as string;
      const type = (el.getAttribute('data-type') as FieldItem['type']) || 'string';
      const required = (el.getAttribute('data-required') || 'false') === 'true';
      const maxLen = parseInt(el.getAttribute('data-maxlen') || '0', 10);
      const min = el.getAttribute('data-min');
      const max = el.getAttribute('data-max');
      const decimals = parseInt(el.getAttribute('data-decimals') || '0', 10);
      const enumList = (el.getAttribute('data-enum') || '')
        .split('|')
        .map(s => s.trim())
        .filter(Boolean);
      const labelAttr = el.getAttribute('data-label');
      const label = labelAttr && labelAttr.trim().length > 0 ? labelAttr : key;
      items.push({
        key,
        value: getElementRawText(el),
        type,
        required,
        maxLen,
        min,
        max,
        decimals,
        enumList: enumList.length ? enumList : undefined,
        label
      });
    });
    setFields(items);
    const updatedHtml = getSerializedInnerHtml(root);
    setHtmlContent(updatedHtml);
    setTemplatePreviewHtml(updatedHtml);
    setListEditVisible(true);
  };

  const applyListEdit = () => {
    const root = containerRef.current;
    if (!root) return;
    const nodes = Array.from(root.querySelectorAll<HTMLElement>('[data-editable="true"][data-var]'));
    fields.forEach(f => {
      const el = nodes.find(n => n.getAttribute('data-var') === f.key);
      if (!el || el.hasAttribute(APPENDIX_REGISTRY_ATTR)) return;
      const val = (f.value || '').trim();
      if (val.length === 0) {
        el.innerHTML = '&nbsp;';
      } else {
        setElementContent(el, f.value);
      }
      validateAndClamp(el);
      el.setAttribute('contenteditable', 'true');
      el.classList.add('editable');
    });
    const updatedHtml = getSerializedInnerHtml(root);
    setHtmlContent(updatedHtml);
    setListEditVisible(false);
  };

  return (
    <div style={{ width: '100%', position: 'relative' }}>
      {adminMode && (
        <div style={{
          position: 'sticky',
          top: '0px',
          zIndex: 500,
          backgroundColor: 'white',
          borderBottom: '1px solid #dee2e6',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <Accordion activeIndex={accordionActiveIndex as any} onTabChange={(e) => {
            const idx = Array.isArray(e.index) ? (e.index[0] ?? null) : (e.index ?? null);
            setAccordionActiveIndex(typeof idx === 'number' ? idx : null);
          }}>
            <AccordionTab header="HTML шаблона">
              <div>
                <ToggleButton
                  checked={highlightAllElements}
                  onChange={(e) => setHighlightAllElements(e.value)}
                  onIcon="pi pi-eye"
                  onLabel=""
                  offIcon="pi pi-eye-slash"
                  offLabel=""
                  tooltip="Подсвечивать границы всех элементов при наведении (Alt+M)"
                  tooltipOptions={{ position: 'top' }}
                />
              </div>
              <CodeMirror
                value={templatePreviewHtml || (containerRef.current ? getSerializedInnerHtml(containerRef.current) : htmlContent)}
                onChange={(value) => {
                  setHtmlContent(value);
                  setTemplatePreviewHtml(value);
                }}
                extensions={[html()]}
                height="260px"
                onCreateEditor={(view) => { editorViewRef.current = view; setEditorReady(true); }}
                basicSetup={{
                  lineNumbers: true,
                  highlightActiveLineGutter: true,
                  highlightSpecialChars: true,
                  history: true,
                  foldGutter: true,
                  drawSelection: true,
                  dropCursor: true,
                  allowMultipleSelections: true,
                  indentOnInput: true,
                  syntaxHighlighting: true,
                  bracketMatching: true,
                  closeBrackets: true,
                  autocompletion: true,
                  rectangularSelection: true,
                  crosshairCursor: true,
                  highlightActiveLine: true,
                  highlightSelectionMatches: true,
                  closeBracketsKeymap: true,
                  defaultKeymap: true,
                  searchKeymap: true,
                  historyKeymap: true,
                  foldKeymap: true,
                  completionKeymap: true,
                  lintKeymap: true,
                }}
                style={{
                  fontSize: '12px',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                }}
                placeholder="Введите HTML код шаблона..."
              />

            </AccordionTab>
          </Accordion>
        </div>
      )}

      {/* {!adminMode && !readonly && (
        <div style={{ marginBottom: 16 }}>
          <Button icon="pi pi-list" label="Редактировать списком" onClick={openListEdit} size="small" severity="secondary" className="mt-2 mr-2" />
        </div>
      )} */}

      <div style={{ maxWidth: isLandscape ? '29.7cm' : '21cm', minHeight: isLandscape ? '21cm' : '29.7cm' }} className='document-editor'>
        {!adminMode && (
          <OverlayPanel ref={enumOverlayRef} dismissable showCloseIcon>
            <div style={{ minWidth: 240 }}>
              <Dropdown
                value={enumSelected}
                options={enumOptions.map(v => ({ label: v, value: v }))}
                placeholder="Выберите значение"
                className="w-100"
                onChange={(e) => {
                  const next = e.value as string;
                  setEnumSelected(next);
                  const el = enumTargetElRef.current;
                  if (el) {
                    setElementContent(el, next);
                    validateAndClamp(el);
                    const root = containerRef.current;
                    if (root) {
                      const updatedHtml = getSerializedInnerHtml(root);
                      setHtmlContent(updatedHtml);
                    }
                  }
                  enumOverlayRef.current?.hide();
                }}
              />
            </div>
          </OverlayPanel>
        )}

        {adminMode && (
          <OverlayPanel ref={actionOverlayRef} dismissable showCloseIcon>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button icon="pi pi-pencil" rounded aria-label="Редактировать" tooltip='Редактировать' onClick={() => {
                const el = actionTargetElRef.current;
                if (!el) return;
                actionOverlayRef.current?.hide();
                openAdminForElement(el);
              }} />
              <Button icon="pi pi-code" rounded aria-label="Показать код" tooltip='Показать код' severity="secondary" onClick={() => {
                const el = actionTargetElRef.current;
                if (!el) return;
                setAccordionActiveIndex(0);
                const html = containerRef.current ? getSerializedInnerHtml(containerRef.current) : '';
                const outer = getSerializedInnerHtml(el);
                const start = html.indexOf(outer);
                if (start >= 0) {
                  setPendingSelection({ from: start, to: start + outer.length });
                } else {
                  setPendingSelection(null);
                }
                setTimeout(() => applyPendingSelectionNow(), 0);
                actionOverlayRef.current?.hide();
              }} />
            </div>
          </OverlayPanel>
        )}

        <div
          ref={containerRef}
          className={`page-content${adminMode && highlightAllElements ? ' admin-mode' : ''}`}
          style={{
            padding: '20px',
            minHeight: '200px',
            backgroundColor: 'white',
            border: '1px solid #ddd',
            borderRadius: '4px'
          }}
          dangerouslySetInnerHTML={dangerouslySetInnerHTML}
        />

        {rowButtons.map((button, index) => {
          // Создаем уникальный ключ на основе типа и позиции элемента в DOM
          const rowIndex = Array.from(button.element.parentElement?.children || []).indexOf(button.element);
          const uniqueKey = `${button.type}-${rowIndex}-${index}`;

          return createPortal(
            <div
              key={uniqueKey}
              style={{
                position: 'fixed',
                left: `${button.position.left}px`,
                top: `${button.position.top}px`,
                transform: 'translateY(-50%)',
              }}
            >
              {button.type === 'template' ? (
                <Button
                  icon="pi pi-plus"
                  rounded
                  size="small"
                  tooltip="Добавить строку"
                  tooltipOptions={{ position: 'right' }}
                  onClick={() => {
                    const root = containerRef.current;
                    if (root && (root as any).__handleAddRow) {
                      (root as any).__handleAddRow(button.element);
                    }
                  }}
                  style={{
                    width: '24px',
                    height: '24px',
                    padding: 0,
                  }}
                />
              ) : (
                <Button
                  icon="pi pi-times"
                  rounded
                  size="small"
                  severity="danger"
                  tooltip="Удалить строку"
                  tooltipOptions={{ position: 'right' }}
                  onClick={() => {
                    const root = containerRef.current;
                    if (root && (root as any).__handleRemoveRow) {
                      (root as any).__handleRemoveRow(button.element);
                    }
                  }}
                  style={{
                    width: '24px',
                    height: '24px',
                    padding: 0,
                  }}
                />
              )}
            </div>,
            document.body
          );
        })}

        {strikethroughButtons.map((button, index) => {
          const isStriked = button.element.classList.contains('z-strikethrough');
          const elementIndex = Array.from(button.element.parentElement?.children || []).indexOf(button.element);
          const uniqueKey = `strikethrough-${elementIndex}-${index}`;

          return createPortal(
            <div
              key={uniqueKey}
              style={{
                position: 'fixed',
                left: `${button.position.left}px`,
                top: `${button.position.top}px`,
                transform: 'translateY(-50%)',
              }}
            >
              <Button
                icon={isStriked ? "pi pi-undo" : "pi pi-times-circle"}
                rounded
                size="small"
                severity={isStriked ? "secondary" : "warning"}
                tooltip={isStriked ? "Убрать зачеркивание" : "Зачеркнуть блок (Z)"}
                tooltipOptions={{ position: 'right' }}
                onClick={() => {
                  const root = containerRef.current;
                  if (root && (root as any).__handleToggleStrikethrough) {
                    (root as any).__handleToggleStrikethrough(button.element);
                  }
                }}
                style={{
                  width: '24px',
                  height: '24px',
                  padding: 0,
                }}
              />
            </div>,
            document.body
          );
        })}

        {lineThroughButtons.map((button, index) => {
          const isStriked = button.element.classList.contains('z-line-through');
          const elementIndex = Array.from(button.element.parentElement?.children || []).indexOf(button.element);
          const uniqueKey = `line-through-${elementIndex}-${index}`;

          return createPortal(
            <div
              key={uniqueKey}
              style={{
                position: 'fixed',
                left: `${button.position.left}px`,
                top: `${button.position.top}px`,
                transform: 'translateY(-50%)',
              }}
            >
              <Button
                icon={isStriked ? "pi pi-undo" : "pi pi-minus-circle"}
                rounded
                size="small"
                severity={isStriked ? "secondary" : "info"}
                tooltip={isStriked ? "Убрать зачеркивание" : "Зачеркнуть линией"}
                tooltipOptions={{ position: 'right' }}
                onClick={() => {
                  const root = containerRef.current;
                  if (root && (root as any).__handleToggleLineThrough) {
                    (root as any).__handleToggleLineThrough(button.element);
                  }
                }}
                style={{
                  width: '24px',
                  height: '24px',
                  padding: 0,
                }}
              />
            </div>,
            document.body
          );
        })}

        <AppendixRegistryDialog
          visible={appendixRegistryVisible}
          rows={appendixRegistryRows}
          setRows={setAppendixRegistryRows}
          onHide={() => setAppendixRegistryVisible(false)}
          onApply={applyAppendixRegistry}
        />

        <ListEditDialog
          visible={listEditVisible}
          fields={fields}
          setFields={setFields}
          onHide={() => setListEditVisible(false)}
          onApply={applyListEdit}
        />

        <AdminBlockEditorDialog
          visible={adminDialogVisible}
          adminForm={adminForm}
          onHide={handleAdminHide}
          onDelete={deleteSelectedElement}
          onSave={submitTemplateToServer}
          onChange={handleAdminFormChange}
        />
      </div>

      <style>{`
         @media print {
           @page {
             size: A4 ${isLandscape ? 'landscape' : 'portrait'};
             margin: 0;
           }
         }
 
        .editable {
          background-color: #f0f9ff;
          border: 1px dashed #3b82f6;
          padding: 2px 4px;
          cursor: text;
          transition: all 0.2s ease;
        }
        
        .editable:hover {
          background-color: #dbeafe;
          border-color: #2563eb;
        }
        
        .editable:focus {
          background-color: #fff;
          border: 2px solid #2563eb;
          outline: none;
        }
        
        .editable[aria-invalid="true"] {
          background-color: #fef2f2;
          border-color: #ef4444;
        }
        
        .page-content.admin-mode * {
          outline: 1px dashed rgba(59, 130, 246, 0.3);
        }
        
        .page-content.admin-mode *:hover {
          outline: 2px solid rgba(37, 99, 235, 0.6);
          background-color: rgba(219, 234, 254, 0.2);
        }
        
        .template-variable-chip {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 1.15em;
          height: 1.15em;
          vertical-align: text-bottom;
          margin: 0 1px;
          padding: 0;
          border: 1px solid #2563eb;
          border-radius: 3px;
          background: #eff6ff;
          color: #2563eb;
          cursor: pointer;
          user-select: none;
          line-height: 1;
          position: relative;
          flex-shrink: 0;
        }

        .template-variable-chip::before {
          content: '';
          display: block;
          width: 0.75em;
          height: 0.75em;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%232563eb' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M8 9l-4 4 4 4M16 9l4 4-4 4M14 4l-4 16'/%3E%3C/svg%3E");
          background-size: contain;
          background-repeat: no-repeat;
          background-position: center;
        }

        /* Tooltip стили */
        [data-tooltip] {
          position: relative;
        }
        
        [data-tooltip]:hover::after {
          content: attr(data-tooltip);
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          margin-bottom: 8px;
          padding: 6px 12px;
          background-color: rgba(0, 0, 0, 0.9);
          color: white;
          font-size: 12px;
          border-radius: 4px;
          white-space: nowrap;
          z-index: 1000;
          pointer-events: none;
        }
        
        [data-tooltip]:hover::before {
          content: '';
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          margin-bottom: 2px;
          border: 6px solid transparent;
          border-top-color: rgba(0, 0, 0, 0.9);
          z-index: 1000;
          pointer-events: none;
        }
        
        /* Стили для строк таблицы с возможностью копирования */
        tr[data-row-template],
        tr[data-removable] {
          position: relative;
        }

        .z-strikethrough {
          position: relative;
        }
        .z-strikethrough * {
          opacity: 0;
        }
        .z-strikethrough::before {
          content: '';
          position: absolute;
          inset: 0;
          pointer-events: none;
          background: no-repeat center / 100% 100% url("data:image/svg+xml;utf8,\
        <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1 1' preserveAspectRatio='none'>\
          <line x1='0.05' y1='0.15' x2='0.95' y2='0.15' stroke='rgba(0,0,0,1)' stroke-width='0.01' />\
          <line x1='0.95' y1='0.15' x2='0.05' y2='0.85' stroke='rgba(0,0,0,1)' stroke-width='0.01' />\
          <line x1='0.2' y1='0.5' x2='0.8' y2='0.5' stroke='rgba(0,0,0,1)' stroke-width='0.01' />\
          <line x1='0.05' y1='0.85' x2='0.95' y2='0.85' stroke='rgba(0,0,0,1)' stroke-width='0.01' />\
        </svg>");
        }
        .z-line-through {
          text-decoration: line-through;
        }
        .z-line-through * {
          text-decoration: line-through;
        }
      `}</style>
    </div>
  );
});

DocumentEditorComponent.displayName = 'DocumentEditorComponent';

